import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { Payment } from 'types';
import { Namespace } from 'enums';
import { DATE_FORMAT, DATE_PERIOD_FORMAT } from 'constants-module';

export const useExportPaymentsConfigs = () => {
  const { t } = useTranslation();

  const columns = useMemo(
    () => [
      { name: t('employee'), filterButton: true },
      { name: t('legalEntity'), filterButton: true },
      { name: t('department'), filterButton: true },
      { name: t('period'), filterButton: true },
      { name: t('applicationCreatedOn'), filterButton: true },
      { name: t('status'), filterButton: true },
      { name: t('compensationType'), filterButton: true },
      { name: t('employeeComment'), filterButton: true },
      { name: t('reviewerComment'), filterButton: true },
      { name: t('billedOn'), filterButton: true },
      { name: t('originalPaymentSum'), filterButton: true },
      { name: t('requestCurrency'), filterButton: true },
      { name: t('requestToRuleCurrencyRate'), filterButton: true },
      { name: t('ruleToEmployeeCurrencyRate'), filterButton: true },
      { name: t('requestToEmployeeCurrencyRate'), filterButton: true },
      { name: t('totalSum'), filterButton: true },
      { name: t('employeeCurrency'), filterButton: true },
      { name: t('error'), filterButton: true },
      { name: t('employeeStatus'), filterButton: true },
    ],
    [t],
  );

  const getRows = useCallback(
    (payments: Payment[]) => {
      return payments.map((payment) => {
        return [
          payment.employeeLogin.toLowerCase(),
          payment.legalEntity,
          payment.department,
          dayjs(`${payment.period.year}-${payment.period.month}`).format(DATE_PERIOD_FORMAT),
          new Date(payment.createdOn),
          t(payment.requestStatus, { ns: Namespace.CompensationStatuses }),
          t(payment.compensationRequestType, { ns: Namespace.CompensationTypes }),
          payment.employeeComment,
          payment.administratorComment,
          payment.billedOn ? dayjs(payment.billedOn).format(DATE_FORMAT) : '',
          payment.originalPaymentSum,
          payment.requestCurrency,
          payment.requestToRuleCurrencyRate,
          payment.ruleToEmployeeCurrencyRate,
          payment.requestToEmployeeCurrencyRate,
          payment.calculatedSum,
          payment.employeeCurrency,
          payment.error,
          t(payment.employmentStatus, { ns: Namespace.EmployeeStatuses }),
        ];
      });
    },
    [t],
  );

  const columnsConfig = useMemo(
    () => [
      { width: 22 },
      { width: 20 },
      { width: 20 },
      { width: 18, style: { numFmt: 'MMMM YYYY' } },
      { width: 18, style: { numFmt: 'dd.mm.yyyy' } },
      { width: 16 },
      { width: 32 },
      { width: 26 },
      { width: 26 },
      { width: 16, style: { numFmt: 'dd.mm.yyyy' } },
      { width: 18 },
      { width: 18 },
      { width: 30 },
      { width: 30 },
      { width: 30 },
      { width: 18 },
      { width: 20 },
      { width: 50 },
    ],
    [],
  );

  return { columns, columnsConfig, getRows };
};
